import React, { useCallback, useEffect, useState } from 'react';
import { Box, Chip, Paper, Tooltip, Typography } from '@mui/material';
import { useAuth } from '../context/AuthContext';

// Schema: Futures_Trading/Docs/DASHBOARD.md §3.4 (logs/dashboard_status.json, schema_version 1)
interface FuturesPosition {
  side: string;
  qty: number;
  entry: number | null;
  stop: number | null;
  initial_stop: number | null;
  target: number | null;
  stage: string | null;
  filled: boolean;
  r_points: number | null;
  r_now: number | null;
  peak_r: number | null;
  locked_r: number | null;
}

interface FuturesSymbol {
  contract: string;
  range: { source: string | null; high: number; low: number; mid: number } | null;
  price: number | null;
  quote_age_s: number | null;
  feed_status: string | null;
  state: string | null;
  direction: string | null;
  excluded: boolean;
  trades_today: number;
  position: FuturesPosition | null;
}

interface FuturesStatus {
  schema_version: number;
  updated_at_et: string;
  service_version: string;
  session: {
    equity: number;
    equity_start: number;
    day_pnl: number;
    day_pnl_pct: number;
    entries_open: boolean;
    entry_cutoff_et: string;
    entries_stopped: boolean;
    halted: boolean;
    skip_day: boolean;
    skip_day_reason: string | null;
    trades_today: number;
    max_trades_per_symbol: number;
  };
  feed: { stream_connected: boolean; last_message_age_s: number | null };
  symbols: Record<string, FuturesSymbol>;
}

interface StatusResponse {
  exists: boolean;
  status: FuturesStatus | null;
  age_s: number | null;
  stale: boolean;
}

const POINT_VALUES: Record<string, number> = { MES: 5, ES: 50, MNQ: 2, NQ: 20 };

const STATE_LABELS: Record<string, string> = {
  NONE: 'waiting for a breakout',
  BREAKOUT_DETECTED: 'breakout — waiting for the retest',
  RETEST_CONFIRMED: 'retest confirmed — waiting for a closed candle',
  SIGNAL_READY: 'signal ready — entering',
};

// null source = range recovered from the bot's retest-state file (contract v4)
const RANGE_SOURCE_LABELS: Record<string, string> = {
  PREMARKET: 'pre-market',
  RTH_FALLBACK: 'RTH 09:30–09:45',
  RTH: 'RTH 09:30–09:45',
  '': 'recovered',
};

const STAGE_LABELS: Record<string, string> = {
  INITIAL: 'initial stop',
  BE: 'breakeven',
  PCT_LOCK: '0.10% lock',
  HALF_TRAIL: 'trailing 50% of peak',
};

const fmtPx = (v: number | null | undefined) => (v == null ? '—' : v.toFixed(2));
const fmtR = (v: number | null | undefined) => (v == null ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(2)}R`);
const fmtUsd = (v: number) => `${v >= 0 ? '+' : '−'}$${Math.abs(v).toFixed(2)}`;
const fmtClock = (iso: string) => {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit', second: '2-digit' });
};
const pointValue = (contract: string) => POINT_VALUES[contract.split('.')[0].toUpperCase()] ?? null;

function entriesChip(s: FuturesStatus['session']) {
  if (s.halted) return { label: 'HALTED — daily floor', color: 'error' as const };
  if (s.skip_day) return { label: `skip day${s.skip_day_reason ? `: ${s.skip_day_reason}` : ''}`, color: 'warning' as const };
  if (s.entries_stopped) return { label: 'entries stopped', color: 'error' as const };
  if (s.entries_open) return { label: `entries open until ${s.entry_cutoff_et}`, color: 'success' as const };
  return { label: `entries closed (cutoff ${s.entry_cutoff_et})`, color: 'default' as const };
}

function feedChip(feed: string | null) {
  if (feed === 'LIVE') return { label: 'feed LIVE', color: 'success' as const, tip: 'Quote is moving' };
  if (feed === 'STALE') return { label: 'feed STALE', color: 'error' as const, tip: 'Quote unchanged > 30 s — the bot pauses entries and stop moves' };
  return { label: 'no quote', color: 'default' as const, tip: 'No quote yet (normal before the open, after the cutoff, or right after a restart)' };
}

const StopLadder: React.FC<{ pos: FuturesPosition; price: number | null }> = ({ pos, price }) => {
  const levels: { label: string; value: number; color: string }[] = [];
  if (pos.target != null) levels.push({ label: 'target', value: pos.target, color: '#66bb6a' });
  if (price != null) levels.push({ label: 'price', value: price, color: '#ffffff' });
  if (pos.entry != null) levels.push({ label: 'entry', value: pos.entry, color: '#90caf9' });
  if (pos.stop != null) levels.push({ label: `stop (${STAGE_LABELS[pos.stage ?? ''] ?? pos.stage ?? '—'})`, value: pos.stop, color: '#ffa726' });
  if (pos.initial_stop != null && pos.initial_stop !== pos.stop) levels.push({ label: 'initial stop', value: pos.initial_stop, color: '#ef5350' });
  levels.sort((a, b) => b.value - a.value);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, fontFamily: 'monospace' }}>
      {levels.map((l) => (
        <Box key={l.label} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: l.color, flexShrink: 0 }} />
          <Typography variant="caption" sx={{ fontFamily: 'monospace', minWidth: 70 }}>{l.value.toFixed(2)}</Typography>
          <Typography variant="caption" color="text.secondary">
            {l.label}
            {price != null && l.label !== 'price' && ` (${(l.value - price) >= 0 ? '+' : ''}${(l.value - price).toFixed(2)} pt)`}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

interface Props {
  compact?: boolean;
  pollMs?: number;
}

const FuturesStatusPanel: React.FC<Props> = ({ compact = false, pollMs = 10000 }) => {
  const { token } = useAuth();
  const [resp, setResp] = useState<StatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    if (!token) return;
    try {
      const r = await fetch('/api/instances/FUTURES/futures-status', { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setResp(await r.json());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'fetch failed');
    }
  }, [token]);

  useEffect(() => {
    fetchStatus();
    const id = setInterval(fetchStatus, pollMs);
    return () => clearInterval(id);
  }, [fetchStatus, pollMs]);

  if (error && !resp) {
    return compact ? null : (
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="body2" color="warning.main">Bot status unavailable ({error})</Typography>
      </Paper>
    );
  }
  if (!resp) return null;
  if (!resp.exists || !resp.status) {
    return compact ? null : (
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          No bot status file yet (logs/dashboard_status.json) — it appears once the bot's session starts (~07:55 ET).
        </Typography>
      </Paper>
    );
  }

  const st = resp.status;
  const s = st.session;
  const entries = entriesChip(s);
  const botChip = resp.stale
    ? { label: `bot idle/down · last update ${fmtClock(st.updated_at_et)}`, color: 'warning' as const }
    : { label: `bot live · ${resp.age_s?.toFixed(0) ?? '?'}s ago`, color: 'success' as const };
  const symbols = Object.entries(st.symbols);
  const tradesLabel = `trades ${s.trades_today}/${s.max_trades_per_symbol}`;

  if (compact) {
    return (
      <Box sx={{ mb: 1 }}>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
          <Chip size="small" label={resp.stale ? 'bot idle' : 'bot live'} color={botChip.color} sx={{ fontSize: '0.6rem', height: 18 }} />
          <Chip size="small" label={entries.label} color={entries.color} variant="outlined" sx={{ fontSize: '0.6rem', height: 18 }} />
          <Chip size="small" label={tradesLabel} variant="outlined" sx={{ fontSize: '0.6rem', height: 18 }} />
        </Box>
        {symbols.map(([sym, d]) => {
          const pv = pointValue(d.contract);
          const p = d.position;
          return (
            <Box key={sym} sx={{ opacity: resp.stale ? 0.6 : 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                <Chip size="small" label={d.contract} variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
                {!resp.stale && d.feed_status === 'STALE' && (
                  <Chip size="small" label="FEED STALE" color="error" sx={{ fontSize: '0.6rem', height: 18 }} />
                )}
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  {p
                    ? `${p.side} ${p.qty} @ ${fmtPx(p.entry)} · stop ${fmtPx(p.stop)} (${STAGE_LABELS[p.stage ?? ''] ?? p.stage ?? '—'})`
                    : d.state && d.state !== 'NONE'
                      ? `${d.direction ?? ''} ${STATE_LABELS[d.state] ?? d.state}`.trim()
                      : s.entries_open ? STATE_LABELS.NONE : 'flat'}
                </Typography>
              </Box>
              {p && (
                <Typography variant="caption" sx={{ display: 'block', pl: 0.5, fontSize: '0.65rem', color: (p.r_now ?? 0) >= 0 ? 'success.main' : 'error.main' }}>
                  {fmtR(p.r_now)} now · peak {fmtR(p.peak_r)} · locked {fmtR(p.locked_r)}
                  {pv && p.r_points != null && p.r_now != null && ` (≈ ${fmtUsd(p.r_now * p.r_points * pv * p.qty)})`}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mr: 1 }}>
          Bot live status
        </Typography>
        <Chip size="small" label={botChip.label} color={botChip.color} />
        <Chip size="small" label={st.feed.stream_connected ? 'stream connected' : 'stream DISCONNECTED'} color={st.feed.stream_connected ? 'default' : 'error'} variant="outlined" />
        <Chip size="small" label={entries.label} color={entries.color} variant="outlined" />
        <Chip size="small" label={tradesLabel} variant="outlined" />
        <Typography variant="body2" sx={{ fontWeight: 'bold', color: s.day_pnl >= 0 ? 'success.main' : 'error.main' }}>
          day {fmtUsd(s.day_pnl)} ({s.day_pnl_pct >= 0 ? '+' : ''}{s.day_pnl_pct.toFixed(2)}%)
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
          {st.service_version} · equity ${s.equity.toFixed(2)} (start ${s.equity_start.toFixed(2)})
        </Typography>
      </Box>

      {symbols.map(([sym, d]) => {
        const feed = feedChip(d.feed_status);
        const p = d.position;
        const pv = pointValue(d.contract);
        return (
          <Box key={sym} sx={{ border: '1px solid rgba(255,255,255,0.12)', borderRadius: 1, p: 1.5, mb: 1, opacity: resp.stale ? 0.6 : 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
              <Chip size="small" label={`${sym} · ${d.contract}`} color="primary" />
              <Tooltip title={feed.tip} arrow>
                <Chip size="small" label={feed.label} color={feed.color} />
              </Tooltip>
              {d.excluded && <Chip size="small" label="excluded today" color="warning" variant="outlined" />}
              <Typography variant="body2">
                price <strong>{fmtPx(d.price)}</strong>
                {d.quote_age_s != null && <span style={{ opacity: 0.7 }}> · quote {d.quote_age_s.toFixed(0)}s old</span>}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Opening range</Typography>
                {d.range ? (
                  <Typography variant="body2">
                    {RANGE_SOURCE_LABELS[d.range.source ?? ''] ?? d.range.source} · H {fmtPx(d.range.high)} · M {fmtPx(d.range.mid)} · L {fmtPx(d.range.low)}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    not built (before the open, or bot restarted after the cutoff)
                  </Typography>
                )}
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Setup</Typography>
                <Typography variant="body2">
                  {d.state
                    ? <>{d.direction && <strong>{d.direction} </strong>}{STATE_LABELS[d.state] ?? d.state}</>
                    : <span style={{ opacity: 0.7, fontStyle: 'italic' }}>{s.entries_open ? STATE_LABELS.NONE : 'no setup — entries closed for the day'}</span>}
                </Typography>
              </Box>
            </Box>

            {p && (
              <Box sx={{ mt: 1.5, display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    In trade {p.filled ? '' : '(entry working — not filled yet)'}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {p.side} {p.qty} @ {fmtPx(p.entry)}
                    <Chip size="small" label={STAGE_LABELS[p.stage ?? ''] ?? p.stage ?? '—'} color={p.stage === 'INITIAL' ? 'default' : 'info'} sx={{ ml: 1, height: 20 }} />
                  </Typography>
                  <Typography variant="body2" sx={{ color: (p.r_now ?? 0) >= 0 ? 'success.main' : 'error.main' }}>
                    {fmtR(p.r_now)} now · peak {fmtR(p.peak_r)}
                  </Typography>
                  <Tooltip title="Profit the current stop guarantees if it is hit" arrow>
                    <Typography variant="body2" color="text.secondary">
                      locked {fmtR(p.locked_r)}
                      {pv && p.r_points != null && ` · 1R = ${p.r_points.toFixed(2)} pt ≈ $${(p.r_points * pv * p.qty).toFixed(0)}`}
                    </Typography>
                  </Tooltip>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Stop ladder</Typography>
                  <StopLadder pos={p} price={d.price} />
                </Box>
              </Box>
            )}
          </Box>
        );
      })}

      <Typography variant="caption" color="text.secondary">
        From the bot's logs/dashboard_status.json (rewritten every ~2 s while the session runs) · updated {fmtClock(st.updated_at_et)} ET
        {error && <span style={{ color: '#ffa726' }}> · last refresh failed ({error})</span>}
      </Typography>
    </Paper>
  );
};

export default FuturesStatusPanel;
